import { useQuery } from '@tanstack/react-query';
import React from 'react';

interface UseDrupalCollectionResult<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
}

export function useDrupalCollection<T = Record<string, unknown>>(
  resourceType: string,
  searchParams?: Record<string, string | number | boolean | undefined>
): UseDrupalCollectionResult<T> {
  // Build a stable, normalized querystring and URL once per content change
  const { queryKey, requestUrl } = React.useMemo(() => {
    const qs = new URLSearchParams();
    if (searchParams) {
      const entries = Object.entries(searchParams).filter(([, v]) => typeof v !== 'undefined');
      entries.sort(([a], [b]) => a.localeCompare(b));
      for (const [k, v] of entries) {
        if (k === 'all') {
          const truthy = v === true || v === '1' || v === 1 || v === 'true';
          qs.set('all', truthy ? '1' : '0');
        } else {
          qs.set(k, String(v));
        }
      }
    }
    const qsString = qs.toString();
    // If no searchParams provided, do not issue a request (used to conditionally disable queries)
    const url =
      resourceType && searchParams
        ? `/api/drupal/${encodeURIComponent(resourceType)}${qsString ? `?${qsString}` : ''}`
        : '';
    return {
      queryKey: ['drupal-collection', resourceType, qsString] as const,
      requestUrl: url,
    };
  }, [resourceType, searchParams]);

  const { data, isLoading, error } = useQuery<{ ok?: boolean; data?: T[] }, Error>({
    queryKey,
    enabled: Boolean(requestUrl),
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    queryFn: async () => {
      const res = await fetch(requestUrl!, { cache: 'no-store' });
      if (!res.ok) throw new Error('Failed to fetch collection');
      return (await res.json()) as { ok?: boolean; data?: T[] };
    },
  });

  const items = React.useMemo(() => (Array.isArray(data?.data) ? (data?.data as T[]) : []), [data]);
  return { items, isLoading, error: error ? error.message : null } as const;
}
